
/**
 * Represents a sequence of words for each level of the game.
 * 
 * The outer array contains one array per day.
 * Each inner array contains 4 words, one for each level:
 * - Index 0: Level 1 (3-letter word)
 * - Index 1: Level 2 (4-letter word)
 * - Index 2: Level 3 (5-letter word)
 * - Index 3: Level 4 (6-letter word)
 */
export type WordSequence = string[][];

/**
 * Represents the level specific information
 */
export type LevelInfo = {
  wordLength: number;
  maxAttempts: number;
};

/**
 * Maps level number (1-4) to level information
 */
export const LEVEL_CONFIG: Record<number, LevelInfo> = {
  1: { wordLength: 3, maxAttempts: 6 },
  2: { wordLength: 4, maxAttempts: 5 },
  3: { wordLength: 5, maxAttempts: 4 },
  4: { wordLength: 6, maxAttempts: 3 }
};

/**
 * Finds the new letter between the current word and the previous word.
 * 
 * @param previousWord The word from the previous level
 * @param currentWord The word from the current level
 * @returns The index of the new letter in the current word
 */
export const findNewLetterIndex = (previousWord: string, currentWord: string): number => {
  if (!previousWord || !currentWord) return 0;
  
  // Special case: If current word is just previous word + a letter at the end
  if (currentWord.length === previousWord.length + 1 && 
      currentWord.startsWith(previousWord)) {
    return previousWord.length;
  }
  
  // For insertions or rearrangements, find letters that aren't in the previous word
  // or are duplicated compared to the previous word
  const charCount = new Map<string, number>();
  
  // Count occurrences in previous word
  for (const char of previousWord) {
    charCount.set(char, (charCount.get(char) || 0) + 1);
  }
  
  // Check each letter in current word to find the new one
  for (let i = 0; i < currentWord.length; i++) {
    const char = currentWord[i];
    const count = charCount.get(char) || 0;
    
    if (count === 0) {
      // This letter doesn't exist in the previous word
      return i;
    }
    
    // Decrement the count to handle duplicate letters
    charCount.set(char, count - 1);
  }
  
  // Fallback (should not reach here if words follow game rules)
  return currentWord.length - 1;
};

/**
 * Gets the new letter between the current word and the previous word.
 * 
 * @param previousWord The word from the previous level
 * @param currentWord The word from the current level
 * @returns The new letter in uppercase
 */
export const getNewLetter = (previousWord: string, currentWord: string): string => {
  if (!previousWord || !currentWord) return currentWord[0]?.toUpperCase() || '';
  
  const index = findNewLetterIndex(previousWord, currentWord);
  return currentWord[index]?.toUpperCase() || '';
};

